import { useState, useCallback } from "react";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import { PdfTemplateType } from "../../../../pdf-templates/document-template.types";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import {
	OrganizationEntityType,
	ClientEntityType,
	BankAccountEntityType,
	PatientEntityType,
	PractitionerEntityType,
} from "@/lib/supabase/supabaseTypes";

interface PdfTestData {
	organization_id: string;
	organization: OrganizationEntityType;
	sample_clients: ClientEntityType[];
	sample_bank_account: BankAccountEntityType | null;
	sample_patient: PatientEntityType | null;
	sample_practitioner: PractitionerEntityType | null;
}

interface GeneratedPdf {
	orgId: string;
	clientId: string;
	clientName: string;
	pdfType: PdfTemplateType;
	base64: string;
	organizationName: string;
	settingsDiff: Record<string, any>;
}

interface GenerationProgress {
	total: number;
	current: number;
	currentOrg: string;
	currentType: string;
	estimatedRemainingMs?: number;
}

export const useGeneratePdfs = () => {
	const [generatedPdfs, setGeneratedPdfs] = useState<GeneratedPdf[]>([]);
	const [generating, setGenerating] = useState(false);
	const [progress, setProgress] = useState<GenerationProgress | null>(null);
	const [error, setError] = useState<string | null>(null);

	const generatePdf = useCallback(
		async (
			orgId: string,
			clientId: string,
			pdfType: PdfTemplateType,
			pdfConfig: any
		): Promise<string> => {
			await new Promise((resolve) => setTimeout(resolve, 100));
			const pdfElement = document.getElementById(
				`pdf-${orgId}-${clientId}-${pdfType}`
			) as HTMLElement;

			if (!pdfElement) {
				throw new Error(
					`PDF element not found for ${orgId}-${clientId}-${pdfType}`
				);
			}

			const drawOptions: any = {
				paperSize: ["210mm", "297mm"],
				keepTogether: "p",
				scale: 0.75,
			};

			// Add custom footer if enabled
			if (pdfConfig?.general?.customFooterEnabled) {
				// Note: createCustomFooterTemplate would need to be imported
				// For now, we'll skip this as it's optional
			}

			const group = await drawDOM(pdfElement, drawOptions);
			const dataUri = await exportPDF(group, {});
			const base64String = dataUri.split(";base64,")[1];

			return base64String;
		},
		[]
	);

	const generateAll = useCallback(
		async (
			selectedOrgs: PdfTestData[],
			selectedTypes: PdfTemplateType[],
			settingsMapping?: Record<string, PdfTemplateType[]>,
			selectedFilters?: string[]
		) => {
			setGenerating(true);
			setError(null);
			setGeneratedPdfs([]);

			try {
				const results: GeneratedPdf[] = [];

				// First pass: determine which PDFs actually exist in the DOM
				const pdfsToGenerate: Array<{
					orgData: PdfTestData;
					client: ClientEntityType;
					pdfType: PdfTemplateType;
				}> = [];

				for (const orgData of selectedOrgs) {
					for (const client of orgData.sample_clients) {
						for (const pdfType of selectedTypes) {
							// Check if DOM element exists
							const elementId = `pdf-${orgData.organization_id}-${client.id}-${pdfType}`;
							if (document.getElementById(elementId)) {
								pdfsToGenerate.push({
									orgData,
									client,
									pdfType,
								});
							}
						}
					}
				}

				const total = pdfsToGenerate.length;
				let current = 0;
				const iterationTimes: number[] = [];
				let lastIterationTime = performance.now();

				// Second pass: actually generate the PDFs
				for (const { orgData, client, pdfType } of pdfsToGenerate) {
					try {
						// Parse PDF config - documents_config is already an object from JSONB
						let pdfConfig;
						if (
							typeof orgData.organization.documents_config ===
								"object" &&
							orgData.organization.documents_config !== null
						) {
							pdfConfig = orgData.organization.documents_config;
						} else if (
							typeof orgData.organization.documents_config ===
							"string"
						) {
							try {
								pdfConfig = JSON.parse(
									orgData.organization.documents_config
								);
							} catch (e) {
								console.warn(
									"Failed to parse documents_config, using empty object",
									e
								);
								pdfConfig = {};
							}
						} else {
							pdfConfig = {};
						}

						const base64 = await generatePdf(
							orgData.organization_id,
							client.id!,
							pdfType,
							pdfConfig
						);

						// Compute relevant settings for this PDF type
						const relevantSettings: Record<string, any> = {};
						if (
							settingsMapping &&
							selectedFilters &&
							selectedFilters.length > 0
						) {
							// Get filters that are relevant for this PDF type
							const relevantFilters = selectedFilters.filter(
								(filter) =>
									settingsMapping[filter]?.includes(pdfType)
							);

							// Extract values from client for relevant filters
							for (const filter of relevantFilters) {
								relevantSettings[filter] =
									client[filter as keyof ClientEntityType];
							}
						}

						results.push({
							orgId: orgData.organization_id,
							clientId: client.id!,
							clientName:
								getClientDisplayName(client) || "Unbenannt",
							pdfType,
							base64,
							organizationName:
								orgData.organization.name || "Unbenannt",
							settingsDiff: relevantSettings,
						});
					} catch (err) {
						console.error(
							`Failed to generate PDF for ${orgData.organization.name} - ${client.id} - ${pdfType}:`,
							err
						);
						// Continue with other PDFs even if one fails
					}

					current++;

					// Track iteration time for moving average
					const currentTime = performance.now();
					const iterationTime = currentTime - lastIterationTime;
					lastIterationTime = currentTime;

					iterationTimes.push(iterationTime);
					if (iterationTimes.length > 10) {
						iterationTimes.shift();
					}

					// Calculate moving average of last 10 iterations
					const avgTimePerPdf =
						iterationTimes.reduce((sum, time) => sum + time, 0) /
						iterationTimes.length;
					const remainingPdfs = total - current;
					const estimatedRemainingMs = avgTimePerPdf * remainingPdfs;

					setProgress({
						total,
						current,
						currentOrg: orgData.organization.name || "Unbenannt",
						currentType: pdfType.toUpperCase(),
						estimatedRemainingMs,
					});
				}

				setGeneratedPdfs(results);
			} catch (err) {
				console.error("Error generating PDFs:", err);
				setError(
					err instanceof Error
						? err.message
						: "Fehler beim Generieren der PDFs"
				);
			} finally {
				setGenerating(false);
				setProgress(null);
			}
		},
		[generatePdf]
	);

	const clearAll = useCallback(() => {
		setGeneratedPdfs([]);
		setError(null);
		setProgress(null);
	}, []);

	return {
		generatedPdfs,
		generating,
		progress,
		error,
		generateAll,
		clearAll,
	};
};
